import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useCarnivalWebSocket from "hooks/useCarnivalWebSocket";
import useUserId from "hooks/useUserId";
import { useTranslation } from "react-i18next";

const InterviewRoomPage = () => {
  const [room, setRoom] = useState(null);
  const [queueInfo, setQueueInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { roomId } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const { userId: userIdObject } = useUserId();
  const { t } = useTranslation();

  // Extract user ID properly
  const userId = userIdObject?.userId || userIdObject?.id || userIdObject;

  // Store the decoded roomId to use consistently
  const [decodedRoomId, setDecodedRoomId] = useState(null);

  // WebSocket connection for real-time queue updates
  const { queueInfo: wsQueueInfo, isConnected } = useCarnivalWebSocket(
    room?.carnival,
    userId,
    {
      onQueueUpdate: (data) => {
        console.log("Queue update received in InterviewRoom:", data);
        // Refresh queue position when queue changes
        if (room && decodedRoomId) {
          fetchQueuePosition();
        }
      },
      onParticipantUpdate: (data) => {
        console.log("Participant update received in InterviewRoom:", data);
        // Refresh queue position when participants change
        if (room && decodedRoomId) {
          fetchQueuePosition();
        }
      },
      debug: process.env.NODE_ENV === "development",
    }
  );

  useEffect(() => {
    // First, decode the roomId and store it
    try {
      const decoded = atob(roomId);
      setDecodedRoomId(decoded);
    } catch (error) {
      console.error("Error decoding room ID:", error);
      // If decoding fails, the ID might already be in decoded format
      setDecodedRoomId(roomId);
    }
  }, [roomId]);

  useEffect(() => {
    // Only proceed when we have a decoded room ID
    if (!decodedRoomId) return;

    const fetchRoomData = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}opportunities/api/interview-rooms/${decodedRoomId}/`,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken"),
            },
          }
        );
        setRoom(response.data);
      } catch (error) {
        console.error("Error fetching room data:", error);
        navigate("/joincaravan"); // Navigate to the correct path /join instead of /dashboard
      }
    };

    fetchRoomData();
  }, [decodedRoomId, navigate, apiUrl]);

  // Extract fetchQueuePosition function to be used by WebSocket callbacks
  const fetchQueuePosition = async () => {
    if (!decodedRoomId) return;

    try {
      const response = await axios.get(
        `${apiUrl}opportunities/api/interview-rooms/${decodedRoomId}/queue_position/`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        }
      );
      setQueueInfo(response.data);
    } catch (error) {
      console.error("Error fetching queue position:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!room || !decodedRoomId) return;

    // Initial fetch of queue position
    fetchQueuePosition();

    // Note: Removed polling interval - now using WebSocket for real-time updates
    // If WebSocket is not connected, fallback to polling
    let interval;
    if (!isConnected) {
      interval = setInterval(fetchQueuePosition, 60000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [room, decodedRoomId, apiUrl, isConnected]);

  // Update queue info when WebSocket provides new data
  useEffect(() => {
    if (wsQueueInfo && wsQueueInfo.has_ticket) {
      setQueueInfo(wsQueueInfo);
    }
  }, [wsQueueInfo]);

  // Handle back button click with the correct path
  const handleBackClick = () => {
    // Navigate to /join route which is the actual dashboard path
    navigate("/joincaravan");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!room) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <IconButton
          onClick={handleBackClick}
          sx={{
            color: "#0017E7",
            "&:hover": {
              backgroundColor: "rgba(0, 23, 231, 0.04)",
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">
            {t("candidates.candidatesAhead")}: {queueInfo?.candidates_ahead || 0}
          </Typography>
          {isConnected && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "success.main",
                animation: "pulse 2s infinite",
              }}
              title={t("candidates.realTimeUpdatesActive")}
            />
          )}
          {!isConnected && (
            <Typography variant="caption" sx={{ color: "warning.main", ml: 1 }}>
              {t("candidates.pollingMode")}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ height: "calc(100vh - 170px)" }}>
        <iframe
          src={room.room_link}
          style={{ width: "100%", height: "100%", border: "none" }}
          allow="camera; microphone; display-capture; fullscreen; clipboard-read; clipboard-write"
          title="Interview Room"
        />
      </Box>
    </Box>
  );
};

export default InterviewRoomPage;
